import { ChevronDown, ChevronUp } from 'react-feather';
import { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { GetEventRegistrationsStatsQuery as GetEventRegistrationsStats } from '__generated__/graphql';

import {
  FilterIndicator, StatsListContainer, StatsListFooter, StatsListHeader, StatsListLoader, StatsListRow,
  StickyStatsListCard, StickyStatsListHeader,
} from './Layout';
import GetParticipantFieldStatsQuery from './GetParticipantFieldStatsQuery';
import UI from '../../common/UI';
import useDashboardUrlState from './useDashboardUrlState';
import useLocale from '../../common/useLocale';
import useMediaDevice from '../../common/useMediaDevice';
import useQuery from '../../api/useQuery';

type ParticipantField = GetEventRegistrationsStats['event']['participant_fields'][0];

export interface ParticipantFieldStatsProps {
  participantFields: ParticipantField[];
}

const ParticipantFieldStats = memo(({ participantFields } : ParticipantFieldStatsProps) => {
  const { t } = useTranslation();
  const device = useMediaDevice();

  const limit = 5;
  const [showingAll, setShowingAll] = useState(false);
  const visibleParticipantFields = showingAll ? participantFields : participantFields.slice(0, limit);

  return (
    <StickyStatsListCard id="ParticipantFieldStatsContainer">
      <StickyStatsListHeader>
        <StatsListHeader>
          <UI.FlexContainer sc={{ alignItems: 'center' }}>
            <UI.H4>
              {t('custom_fields')}
            </UI.H4>
            <FilterIndicator sc={{ ml: 2 }} />
          </UI.FlexContainer>
          {!device.isSmall && <UI.Div />}
          <UI.Legend sc={{ small: true }}>
            {t('entries')}
          </UI.Legend>
        </StatsListHeader>
      </StickyStatsListHeader>
      <StatsListContainer>
        {visibleParticipantFields.map((participantField) => (
          <ParticipantFieldRow participantField={participantField} key={participantField.id} />
        ))}
      </StatsListContainer>
      {participantFields.length > limit && (
        <StatsListFooter>
          {!showingAll && (
            <UI.Button
              onClick={() => setShowingAll(true)}
              sc={{ blank: true, size: 'xs', mr: 2 }}
            >
              {t('show_all')}
              {' '}
              <UI.Span sc={{ muted: true }}>
                {`(${participantFields.length})`}
              </UI.Span>
              {' '}
              <UI.Icon sc={{ muted: true }}>
                <ChevronDown />
              </UI.Icon>
            </UI.Button>
          )}
          {showingAll && (
            <UI.Button
              onClick={() => setShowingAll(false)}
              sc={{ blank: true, size: 'xs', mr: 2 }}
            >
              {t('show_less')}
              {' '}
              <UI.Icon sc={{ muted: true }}>
                <ChevronUp />
              </UI.Icon>
            </UI.Button>
          )}
        </StatsListFooter>
      )}
    </StickyStatsListCard>
  );
});

interface ParticipantFieldRowProps {
  participantField: ParticipantField;
}

const ParticipantFieldRow = ({ participantField }: ParticipantFieldRowProps) => {
  const { t } = useTranslation();
  const { formatInteger } = useLocale();
  const device = useMediaDevice();

  const hasExtraStats = participantField.choices.length > 0;
  const [showingExtraStats, setShowingExtraStats] = useState(false);

  const toggleExtraStats = () => setShowingExtraStats((showing) => !showing);

  return (
    <>
      <StatsListRow>
        <UI.Div style={{ minWidth: 0 }}>
          <UI.FlexContainer>
            <UI.Truncate>
              <UI.Span sc={{ color: 'gray.800' }}>
                <UI.Icon sc={{ mr: 1 }}>
                  <UI.StatusIndicator sc={{ background: participantField.enabled ? 'success' : 'gray.300' }} />
                </UI.Icon>
                {participantField.title}
              </UI.Span>
            </UI.Truncate>
            {hasExtraStats && (
              <UI.Span>
                <UI.Button
                  sc={{ blank: true, size: 'xs', my: -0.75, ml: 1 }}
                  onClick={() => toggleExtraStats()}
                  aria-label={t('toggle_name', { name: participantField.title })}
                >
                  <UI.Icon>
                    {showingExtraStats && <ChevronUp />}
                    {!showingExtraStats && <ChevronDown />}
                  </UI.Icon>
                </UI.Button>
              </UI.Span>
            )}
          </UI.FlexContainer>
        </UI.Div>
        {!device.isSmall && <UI.Div />}
        <UI.Div sc={{ noWrap: true }}>
          {formatInteger(participantField.field_entry_count)}
        </UI.Div>
      </StatsListRow>
      {showingExtraStats && <ExtraParticipantFieldStats participantField={participantField} />}
    </>
  );
};

const ExtraParticipantFieldStats = ({ participantField: initialParticipantField }: ParticipantFieldRowProps) => {
  const { formatInteger } = useLocale();
  const device = useMediaDevice();

  const [urlState] = useDashboardUrlState();

  const { data } = useQuery(
    GetParticipantFieldStatsQuery, {
      variables: {
        id: initialParticipantField.id,
        tickets: urlState.tickets,
      },
      pollInterval: 30000,
    },
  );

  if (!data) {
    return <StatsListLoader />;
  }

  const { participantField } = data;

  return (
    <>
      {participantField.choices.length > 0 && participantField.choices.map((choice) => (
        <StatsListRow sc={{ background: 'gray.50' }} key={choice.id}>
          <UI.Div sc={{ pl: 4 }} style={{ minWidth: 0 }}>
            <UI.Truncate>
              {choice.title}
            </UI.Truncate>
          </UI.Div>
          {!device.isSmall && <UI.Div />}
          <UI.Div sc={{ noWrap: true }}>
            {formatInteger(choice.choice_entry_count)}
          </UI.Div>
        </StatsListRow>
      ))}
    </>
  );
};

export default ParticipantFieldStats;
